import { clsx, type ClassValue } from "clsx"
import { twMerge } from "tailwind-merge"
import { configs, DefaultImage } from "../modules/ui-builder/external-components"

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}
export function generateImagePath(
  inputPath: string | null | undefined,
  sizeType: "thumbnail" | "small" | "medium" | "large" | "original",
) {
  if (!inputPath) {
    return DefaultImage
  }

  // Define allowed size types
  const allowedSizeTypes = ["thumbnail", "small", "medium", "large", "original"]

  // Validate sizeType
  if (!allowedSizeTypes.includes(sizeType)) {
    throw new Error(`Invalid size type. Allowed values are: ${allowedSizeTypes.join(", ")}`)
  }

  // Extract the filename from the input path
  const filename = inputPath.split("/").pop() as string

  // Check if the file is a PNG
  const isPng = filename.toLowerCase().endsWith(".png")

  // If it's a PNG, return the original file path
  if (isPng) {
    return `${configs.API_URL}${inputPath}`
  }

  // Construct the new path with the size type
  return `${configs.API_URL}${inputPath.replace(filename, `${sizeType}_${filename}`)}`
}

export const replaceImageUrls = (content: string) => {
  const currentDomain = import.meta.env.VITE_OLD_IMAGE_URL
  const newDomain = import.meta.env.VITE_URL_IMAGES_PRODUCTION

  if (!currentDomain || !newDomain) {
    throw new Error("Environment variables CURRENT and DOMAIN must be set")
  }

  const regex = new RegExp(`${currentDomain.replace(/\/$/, "")}(/?)`, "g")

  const updatedContent = content.replace(regex, `${newDomain}/`)

  return updatedContent
}

const HEADING_INLINE_STYLES: Record<string, { fontWeight: string; fontSize: string; lineHeight: string }> = {
  h1: { fontWeight: "700", fontSize: "2.25rem", lineHeight: "2.5rem" },
  h2: { fontWeight: "700", fontSize: "1.875rem", lineHeight: "2.25rem" },
  h3: { fontWeight: "700", fontSize: "1.5rem", lineHeight: "2rem" },
  h4: { fontWeight: "600", fontSize: "1.25rem", lineHeight: "1.75rem" },
  h5: { fontWeight: "600", fontSize: "1.125rem", lineHeight: "1.75rem" },
  h6: { fontWeight: "600", fontSize: "1rem", lineHeight: "1.5rem" },
}

// CKEditor only outputs bare <h1>-<h6> tags; renderers outside this app's .ck-content CSS
// (the public site, other consumers of the saved content) would show them unstyled otherwise.
export const applyHeadingInlineStyles = (content: string): string => {
  const tempDiv = document.createElement("div")
  tempDiv.innerHTML = content

  Object.entries(HEADING_INLINE_STYLES).forEach(([tag, { fontWeight, fontSize, lineHeight }]) => {
    tempDiv.querySelectorAll(tag).forEach(el => {
      const heading = el as HTMLElement
      heading.style.fontWeight = fontWeight
      heading.style.fontSize = fontSize
      heading.style.lineHeight = lineHeight
    })
  })

  return tempDiv.innerHTML
}

export const convertOembedToIframe = (content: string): string => {
  const tempDiv = document.createElement("div")
  tempDiv.innerHTML = content

  const oembeds = tempDiv.querySelectorAll("oembed[url]")

  oembeds.forEach((oembed) => {
    const url = oembed.getAttribute("url")
    if (!url) return

    const figure = oembed.parentElement
    let iframe: HTMLIFrameElement | null = null

    if (url.includes("youtube.com") || url.includes("youtu.be")) {
      const videoId = extractYouTubeId(url)
      if (videoId) {
        iframe = document.createElement("iframe")
        iframe.src = `https://www.youtube.com/embed/${videoId}`
        iframe.setAttribute("allowfullscreen", "true")
        iframe.setAttribute("loading", "lazy")
        iframe.style.cssText = "position:absolute;top:0;left:0;width:100%;height:100%;border:0;"
      }
    } else if (url.includes("facebook.com/reel/")) {
      const match = url.match(/reel\/([a-zA-Z0-9]+)/)
      if (match) {
        iframe = document.createElement("iframe")
        iframe.src = `https://www.facebook.com/plugins/video.php?href=${encodeURIComponent(url)}`
        iframe.setAttribute("allowfullscreen", "true")
        iframe.setAttribute("loading", "lazy")
        iframe.style.cssText = "position:absolute;top:0;left:0;width:100%;height:100%;border:0;"
      }
    } else if (url.includes("facebook.com") && url.includes("/videos/")) {
      const match = url.match(/\/videos\/([a-zA-Z0-9]+)/)
      if (match) {
        iframe = document.createElement("iframe")
        iframe.src = `https://www.facebook.com/plugins/video.php?href=${encodeURIComponent(url)}`
        iframe.setAttribute("allowfullscreen", "true")
        iframe.setAttribute("loading", "lazy")
        iframe.style.cssText = "position:absolute;top:0;left:0;width:100%;height:100%;border:0;"
      }
    } else if (url.includes("tiktok.com")) {
      iframe = document.createElement("iframe")
      iframe.src = `https://www.tiktok.com/embed/${extractTiktokId(url)}`
      iframe.setAttribute("allowfullscreen", "true")
      iframe.setAttribute("loading", "lazy")
      iframe.style.cssText = "position:absolute;top:0;left:0;width:100%;height:100%;border:0;"
    }

    if (iframe && figure) {
      const wrapper = document.createElement("div")
      wrapper.style.cssText = "position:relative;padding-bottom:56.25%;height:0;overflow:hidden;margin:16px 0;"
      iframe.style.cssText = "position:absolute;top:0;left:0;width:100%;height:100%;border:0;"
      wrapper.appendChild(iframe)
      figure.replaceWith(wrapper)
    }
  })

  return tempDiv.innerHTML
}

const extractYouTubeId = (url: string): string | null => {
  const patterns = [
    /youtube\.com\/watch\?v=([a-zA-Z0-9_-]+)/,
    /youtube\.com\/embed\/([a-zA-Z0-9_-]+)/,
    /youtu\.be\/([a-zA-Z0-9_-]+)/,
    /youtube\.com\/shorts\/([a-zA-Z0-9_-]+)/,
  ]
  for (const pattern of patterns) {
    const match = url.match(pattern)
    if (match) return match[1]
  }
  return null
}

const extractTiktokId = (url: string): string => {
  const match = url.match(/\/video\/(\d+)/)
  return match ? match[1] : ""
}

export type ImageAlignment = "left" | "center" | "right" | "none"

export interface ImageAttributes {
  alt?: string
  caption?: string
  width?: string | number
  height?: string | number
  alignment?: ImageAlignment
}

export const generateImageHtml = (src: string, attributes?: ImageAttributes): string => {
  const { alt = "", caption, width, height, alignment = "none" } = attributes || {}

  const widthAttr = width ? ` width="${width}"` : ""
  const heightAttr = height ? ` height="${height}"` : ""

  const styleParts: string[] = []
  if (alignment === "left") styleParts.push("display: block; margin: 0 auto 16px 0;")
  else if (alignment === "center") styleParts.push("display: block; margin: 0 auto;")
  else if (alignment === "right") styleParts.push("display: block; margin: 0 0 16px auto;")

  const styleAttr = styleParts.length > 0 ? ` style="${styleParts.join(" ")}"` : ""

  const imgTag = `<img src="${src}" alt="${alt}"${widthAttr}${heightAttr}${styleAttr}>`

  if (caption && caption.trim()) {
    return `<figure class="image-caption">${imgTag}<figcaption>${caption.trim()}</figcaption></figure>`
  }

  return imgTag
}
